@if (!(loading$ | async)) {
  <div class="pane flat">
    <sui-toolbar #tb>
      <sui-checkbox
        [checked]="selectAllChecked"
        (checkedChange)="onSelectAllChange($event)"
      >
        {{ "workspace-actions.button.select-all" | translate }}
      </sui-checkbox>
      <app-search-input
        [placeholder]="'workspace-actions.placeholder.search' | translate"
        [value]="filterString"
        (inputChanged)="handleObjectSearchChange($event)"
      ></app-search-input>
      <!-- Apply to selected (command menu) — no leading icon, so it stays labelled -->
      @if (applyActions.length) {
        <sui-button
          variant="secondary"
          [trailingIcon]="['fas', 'caret-down']"
          [suiPopoverTrigger]="applyPanel"
          suiPopoverPlacement="bottom-start"
          #applyTrigger="suiPopoverTrigger"
          [expanded]="applyTrigger.isOpen()"
        >
          {{ applyLabel }}
        </sui-button>
        <ng-template #applyPanel>
          <div suiPopover class="dropdown-menu">
            @for (a of applyActions; track a) {
              <a
                class="dropdown-item"
                (click)="
                  a.action?.();
                  $event.preventDefault();
                  $event.stopPropagation()
                "
              >
                <span class="dropdown-item-link-col1">
                  <span class="dropdown-item-link-label">
                    @if (a.icon) {
                      <div><fa-icon [icon]="a.icon"></fa-icon></div>
                    }
                    <span>{{ a.text }}</span>
                  </span>
                </span>
              </a>
            }
          </div>
        </ng-template>
      }
      <!-- Status (first of the right-aligned group) -->
      <sui-button
        class="right-group-start"
        variant="ghost"
        [leadingIcon]="statusIcon"
        [trailingIcon]="['fas', 'caret-down']"
        [iconOnly]="tb.collapsed()"
        [ariaLabel]="tb.collapsed() ? statusLabel : undefined"
        [suiTooltip]="tb.collapsed() ? statusLabel : ''"
        [suiPopoverTrigger]="statusPanel"
        suiPopoverPlacement="bottom-end"
        #statusTrigger="suiPopoverTrigger"
        [expanded]="statusTrigger.isOpen()"
      >
        {{ tb.collapsed() ? "" : statusLabel }}
      </sui-button>
      <ng-template #statusPanel>
        <div suiPopover class="dropdown-menu">
          @if (tb.collapsed()) {
            <div class="dropdown-heading">
              {{ statusLabel }}
            </div>
          }
          <div
            sui-radio-group
            orientation="vertical"
            [ngModel]="lifecycleStatus"
            [ngModelOptions]="{ standalone: true }"
            (valueChange)="onStatusChange($any($event))"
          >
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              [value]="1"
            >
              <fa-icon [icon]="['fas', 'check']"></fa-icon>
              <span>{{ "workspace-actions.button.active" | translate }}</span>
            </sui-radio>
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              [value]="3"
            >
              <fa-icon [icon]="['fas', 'box-archive']"></fa-icon>
              <span>{{ "workspace-actions.button.archived" | translate }}</span>
            </sui-radio>
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              [value]="4"
            >
              <fa-icon [icon]="['fas', 'trash']"></fa-icon>
              <span>{{ "workspace-actions.button.deleted" | translate }}</span>
            </sui-radio>
          </div>
        </div>
      </ng-template>
      <!-- View -->
      <sui-button
        variant="ghost"
        [leadingIcon]="['fas', 'eye']"
        [trailingIcon]="['fas', 'caret-down']"
        [iconOnly]="tb.collapsed()"
        [ariaLabel]="tb.collapsed() ? viewLabel : undefined"
        [suiTooltip]="tb.collapsed() ? viewLabel : ''"
        [suiPopoverTrigger]="viewPanel"
        suiPopoverPlacement="bottom-end"
        #viewTrigger="suiPopoverTrigger"
        [expanded]="viewTrigger.isOpen()"
      >
        {{ tb.collapsed() ? "" : viewLabel }}
      </sui-button>
      <ng-template #viewPanel>
        <div suiPopover class="dropdown-menu">
          @if (tb.collapsed()) {
            <div class="dropdown-heading">{{ viewLabel }}</div>
          }
          @for (group of viewGroups; track group) {
            @if (group.radioGroup) {
              <div
                sui-radio-group
                orientation="vertical"
                [ngModel]="group.value"
                [ngModelOptions]="{ standalone: true }"
                (valueChange)="onViewRadioChange(group, $event)"
              >
                @for (item of group.items; track item) {
                  <sui-radio
                    class="dropdown-item"
                    indicatorPosition="trailing"
                    [value]="$any(item.value)"
                  >
                    @if (item.icon) {
                      <fa-icon [icon]="item.icon"></fa-icon>
                    }
                    <span>{{ item.text }}</span>
                  </sui-radio>
                }
              </div>
            } @else {
              @for (item of group.items; track item) {
                <sui-checkbox
                  class="dropdown-item"
                  indicatorPosition="trailing"
                  [checked]="!!item.isSelected"
                  (checkedChange)="onViewToggle(item)"
                >
                  @if (item.icon) {
                    <fa-icon [icon]="item.icon"></fa-icon>
                  }
                  <span>{{ item.text }}</span>
                </sui-checkbox>
              }
            }
          }
        </div>
      </ng-template>
      <!-- Sort -->
      <sui-button
        variant="ghost"
        [leadingIcon]="sortDirIcon"
        [trailingIcon]="['fas', 'caret-down']"
        [iconOnly]="tb.collapsed()"
        [ariaLabel]="tb.collapsed() ? sortLabel : undefined"
        [suiTooltip]="tb.collapsed() ? sortLabel : ''"
        [suiPopoverTrigger]="sortPanel"
        suiPopoverPlacement="bottom-end"
        #sortTrigger="suiPopoverTrigger"
        [expanded]="sortTrigger.isOpen()"
      >
        {{ tb.collapsed() ? "" : sortLabel }}
      </sui-button>
      <fa-icon class="sort-field-icon" [icon]="sortFieldIcon"></fa-icon>
      <ng-template #sortPanel>
        <div suiPopover class="dropdown-menu">
          @if (tb.collapsed()) {
            <div class="dropdown-heading">{{ sortLabel }}</div>
          }
          <div
            sui-radio-group
            orientation="vertical"
            [ngModel]="sortField"
            [ngModelOptions]="{ standalone: true }"
            (valueChange)="onSortFieldChange($any($event))"
          >
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              value="title"
            >
              <fa-icon [icon]="['fas', 'text-height']"></fa-icon>
              <span>{{ "workspace-actions.button.title" | translate }}</span>
            </sui-radio>
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              value="time_created"
            >
              <fa-icon [icon]="['fas', 'clock']"></fa-icon>
              <span>{{
                "workspace-actions.button.created-time" | translate
              }}</span>
            </sui-radio>
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              value="time_modified"
            >
              <fa-icon [icon]="['fas', 'clock']"></fa-icon>
              <span>{{
                "workspace-actions.button.modified-time" | translate
              }}</span>
            </sui-radio>
          </div>
          <div class="dropdown-divider"></div>
          <div
            sui-radio-group
            orientation="vertical"
            [ngModel]="sortDirection"
            [ngModelOptions]="{ standalone: true }"
            (valueChange)="onSortDirectionChange($any($event))"
          >
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              value="asc"
            >
              <fa-icon [icon]="['fas', 'sort-amount-up']"></fa-icon>
              <span>{{
                "workspace-actions.button.ascending" | translate
              }}</span>
            </sui-radio>
            <sui-radio
              class="dropdown-item"
              indicatorPosition="trailing"
              value="desc"
            >
              <fa-icon [icon]="['fas', 'sort-amount-down']"></fa-icon>
              <span>{{
                "workspace-actions.button.descending" | translate
              }}</span>
            </sui-radio>
          </div>
        </div>
      </ng-template>
    </sui-toolbar>
    <div class="scrollable-content">
      <div class="card-container-info">
        <div class="title">{{ "workspace.title.dashboards" | translate }}</div>
      </div>
      <div class="card-container mb-containers">
        <app-object-card-placeholder
          [type]="'dashboard'"
        ></app-object-card-placeholder>
        @for (
          dashboard of dashboards$ | async;
          track trackById($index, dashboard)
        ) {
          <app-object-card
            [object]="dashboard"
            [viewConfig]="viewConfig"
            [filterString]="filterString"
            [selectMode]="objectActionService.selectMode"
            [selected]="objectActionService.selectedObjects.has(dashboard.id)"
            [objectActionService]="objectActionService"
          ></app-object-card>
        }
      </div>
      <div class="card-container-info">
        <div class="title">{{ "workspace.title.feeds" | translate }}</div>
      </div>
      <div class="card-container">
        <app-object-card-placeholder
          [type]="'feed'"
        ></app-object-card-placeholder>
        @for (feed of feeds$ | async; track trackById($index, feed)) {
          <app-object-card
            [object]="feed"
            [viewConfig]="viewConfig"
            [filterString]="filterString"
            [selectMode]="objectActionService.selectMode"
            [selected]="objectActionService.selectedObjects.has(feed.id)"
            [objectActionService]="objectActionService"
          ></app-object-card>
        }
      </div>
      <div class="card-container-info">
        <div class="title">{{ "workspace.title.dimensions" | translate }}</div>
      </div>
      <div class="card-container">
        <app-object-card-placeholder
          [type]="'dimension'"
        ></app-object-card-placeholder>
        @for (
          dimension of dimensions$ | async;
          track trackById($index, dimension)
        ) {
          <app-object-card
            [object]="dimension"
            [viewConfig]="viewConfig"
            [selectMode]="objectActionService.selectMode"
            [filterString]="filterString"
            [objectActionService]="objectActionService"
            [selected]="objectActionService.selectedObjects.has(dimension.id)"
          ></app-object-card>
        }
      </div>
    </div>
  </div>
} @else {
  <app-loading></app-loading>
}
